import { useCallback } from 'react';

/**
 * Use native web share if it is available (See docs here: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share)
 *
 * @param {Function} onShareSuccess function called on successfully sharing content
 * @param {Function} onShareError callback function called on error sharing content
 * @example
 * const { isSupported, share } = useWebShare(successFn, errorFn);
 */
const useWebShare = (onShareSuccess?: () => void, onShareError?: (error: unknown) => void) => {
	/**
	 * @param {Object} data Object can take the following:
	 * {title} Title used in the native popup box when sharing
	 * {text} If you want to share a message
	 * {url} The URL you want to share
	 * {files} Files you want to share
	 */
	const share = useCallback(
		async (data: ShareData) => {
			try {
				await navigator.share(data);
				if (onShareSuccess) {
					onShareSuccess();
				}
			} catch (error: unknown) {
				if (onShareError) {
					onShareError(error);
				}
			}
		},
		[onShareError, onShareSuccess]
	);
	const canShare = useCallback((data: ShareData) => {
		return navigator.canShare(data);
	}, []);

	return {
		isSupported: typeof window?.navigator?.share !== 'undefined',
		share,
		canShare
	};
};

export default useWebShare;
